import { useEffect, useState } from 'react';
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';
import { ResponseProvider } from './context';
import { AlertProvider } from './context/AlertContext';
import { SpinnerProvider } from './context/SpinnerContext';
import { ErrorBoundary } from './pages/ErrorBoundary';
import { Login } from './pages/Login';
import { MfaVerify } from './pages/MfaVerify';
import { NewPassword } from './pages/NewPassword';
import { NotFound } from './pages/NotFound';
import { RePassword } from './pages/RePassword';

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route index path="/" element={<Login />} />
      <Route path="/new-password" element={<NewPassword />} />
      <Route path="/mfa-verify" element={<MfaVerify />} />
      <Route path="/reset-password" element={<RePassword />} />

      {/* Layout + http error */}
      <Route
        path="/"
        lazy={async () => import('./layouts/Layout')}
        errorElement={<ErrorBoundary />}
      >
        <Route path="/dashboard" lazy={async () => import('./pages/Dashboard')} />
        <Route path="/environment" lazy={async () => import('./pages/UserPage/UserInfo')} />
        <Route path="/setting" lazy={async () => import('./pages/UserPage/Application')} />
        <Route
          path="/setting/step1"
          lazy={async () => import('./pages/UserPage/SettingStep/Step1')}
        />
        <Route
          path="/setting/step2"
          lazy={async () => import('./pages/UserPage/SettingStep/Step2')}
        />
        <Route
          path="/setting/step3"
          lazy={async () => import('./pages/UserPage/SettingStep/Step3')}
        />
        <Route
          path="/setting/step4"
          lazy={async () => import('./pages/UserPage/SettingStep/Step4')}
        />
        <Route
          path="/setting/step5"
          lazy={async () => import('./pages/UserPage/SettingStep/Step5')}
        />
        <Route path="/tenant" lazy={async () => import('./pages/AdminPage/Tenant')} />
        <Route path="/tenant-list" lazy={async () => import('./pages/AdminPage/TenantList')} />
        <Route path="/sign-up" lazy={async () => import('./pages/AdminPage/Register')} />
        <Route path="/user" lazy={async () => import('./pages/AdminPage/UserList')} />
        <Route
          path="/application-list"
          lazy={async () => import('./pages/AdminPage/ApplicationList')}
        />
        <Route
          path="/ai-input-model"
          lazy={async () => import('./pages/AdminPage/AiInputModelList')}
        />
        <Route path="/sandBox-list" lazy={async () => import('./pages/AdminPage/SandBoxList')} />
        <Route path="/envSetting" lazy={async () => import('./pages/UserPage/EnvSetting')} />
        <Route path="/silo-list" lazy={async () => import('./pages/AdminPage/SiloList')} />
        <Route path="/search-info" lazy={async () => import('./pages/AdminPage/InfoSearch')} />
        <Route path="/company" lazy={async () => import('./pages/UserPage/CompanyManagement')} />
        <Route path="/company-addition" lazy={async () => import('./pages/UserPage/CompanyAdd')} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </>,
  ),
);

export function App() {
  const apiKey = process.env.PENDO_API_KEY;
  const [isScriptLoaded, setIsScriptLoaded] = useState(() => {
    if (!apiKey) return true;
    const src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
    return !!document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
  });
  const [hasScriptError, setHasScriptError] = useState(false);

  useEffect(() => {
    if (!apiKey) return;

    const src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
    let script = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

    if (script) {
      return;
    }

    script = document.createElement('script');
    script.src = src;
    script.async = true;

    const handleLoad = () => setIsScriptLoaded(true);
    const handleError = () => setHasScriptError(true);

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, [apiKey]);

  if (hasScriptError) {
    return (
      <div>
        Failed to load external script, please refresh the page or contact system administrator.
      </div>
    );
  }

  if (!isScriptLoaded) {
    return null;
  }

  return (
    <SpinnerProvider>
      <ResponseProvider>
        <AlertProvider>
          <RouterProvider router={router} />
        </AlertProvider>
      </ResponseProvider>
    </SpinnerProvider>
  );
}
